import React, { useEffect, useState, useMemo, useCallback, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  IconCheck,
  IconX,
  IconListDetails,
  IconEye,
  IconReload,
  IconPhoto,
  IconSearch,
  IconChartPie,
  IconUser,
  IconCircleCheck,
  IconClock,
  IconBuildingStore,
  IconInbox,
  IconAdjustmentsHorizontal,
  IconLayoutGrid,
  IconTable,
  IconCopy,
  IconRotateClockwise,
  IconCalendar,
} from '@tabler/icons-react';
import Layouts from '../../layouts/Layouts';
import { getAuthorizationHeader, http, imageBaseUrl } from '../../config/http';
import { toast } from 'react-toastify';
import moment from 'moment';
import DetailItem from '../Modal/DetailItem';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { useMasterData } from '../../context/MasterDataContext';

export default function VerifikasiHapusEtalaseV2() {
  const { cabangList = [] } = useMasterData() || {};
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [detailItem, setDetailItem] = useState([]);
  const userRole = localStorage.getItem('role');
  const canVerify = userRole === 'admin' || userRole === 'owner' || userRole === 'verifikator';

  // View mode toggle: 'table' (default) vs 'cards'
  const [viewMode, setViewMode] = useState('table');

  // Collapsible Filter Panel Toggle
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // 5-Tab Detail Modal State
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);
  const [selectedPengajuan, setSelectedPengajuan] = useState(null);
  const [modalTab, setModalTab] = useState('ringkasan');
  const [searchModalItem, setSearchModalItem] = useState('');

  // Pagination for Pengajuan Table (Client-side)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Filter States (Default: Semua Data & Semua Tanggal)
  const [statusFilter, setStatusFilter] = useState('');
  const [cabangFilter, setCabangFilter] = useState('');
  const [operatorFilter, setOperatorFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    id: null,
    action: null,
    pengajuan: null,
  });
  const [catatanVerifikasi, setCatatanVerifikasi] = useState('');

  // Date Filter State (Default: null / Semua Tanggal)
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const selectionRange = {
    startDate: startDate || new Date(),
    endDate: endDate || new Date(),
    key: 'selection',
  };

  const handleSelectDate = (date) => {
    setStartDate(date.selection.startDate);
    setEndDate(date.selection.endDate);
  };

  const awal = startDate ? moment(startDate).format('DD-MM-YYYY') : '';
  const akhir = endDate ? moment(endDate).format('DD-MM-YYYY') : '';

  // 1-Tap Copy Helper
  const handleCopyBatchId = (e, batchId) => {
    e.stopPropagation();
    if (navigator.clipboard) {
      navigator.clipboard.writeText(batchId);
      toast.info(`No. Request ${batchId} disalin`, { autoClose: 900 });
    }
  };

  const confirmAction = (pengajuanOrId, action) => {
    let target = null;
    if (typeof pengajuanOrId === 'object' && pengajuanOrId !== null) {
      target = pengajuanOrId;
    } else {
      target = data.find((p) => p.id === pengajuanOrId) || selectedPengajuan;
    }
    setCatatanVerifikasi('');
    setConfirmModal({
      isOpen: true,
      id: target?.id || pengajuanOrId,
      action,
      pengajuan: target,
    });
  };

  const processConfirmAction = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!confirmModal.id || !confirmModal.action) return;

    if (confirmModal.action === 'reject' && !catatanVerifikasi.trim()) {
      toast.warning('Silakan sertakan catatan / alasan penolakan!');
      return;
    }

    await handlePersetujuan(confirmModal.id, confirmModal.action, catatanVerifikasi);
    setConfirmModal({ isOpen: false, id: null, action: null, pengajuan: null });
    setCatatanVerifikasi('');
    setIsOpenDetailModal(false);
  };

  // Modal Restore State
  const [restoreModal, setRestoreModal] = useState({
    isOpen: false,
    pengajuan: null,
    itemIds: [],
    itemsList: [],
  });
  const [alasanRestore, setAlasanRestore] = useState('');
  const [isSubmittingRestore, setIsSubmittingRestore] = useState(false);

  const handleOpenRestoreModal = (pengajuan, itemIds = []) => {
    const items = (pengajuan.items || [])
      .filter((c) => c.status_item !== 'restored' && (itemIds.length === 0 || itemIds.includes(c.item_id)))
      .map((c) => ({
        ...(c.item || c),
        raw_child: c,
      }));
    setAlasanRestore('');
    setRestoreModal({
      isOpen: true,
      pengajuan,
      itemIds,
      itemsList: items,
    });
  };

  const handleProcessRestore = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!alasanRestore.trim()) {
      toast.warning('Silakan isi alasan pengembalian barang ke etalase');
      return;
    }
    setIsSubmittingRestore(true);
    try {
      const payload = {
        alasan: alasanRestore.trim(),
        item_ids: restoreModal.itemIds.length > 0 ? restoreModal.itemIds : undefined,
      };
      const res = await http.post(`/item-deletions/${restoreModal.pengajuan.id}/restore`, payload, {
        headers: { Authorization: getAuthorizationHeader() },
      });
      toast.success(res.data?.message || 'Barang berhasil dikembalikan ke etalase!');
      setRestoreModal({ isOpen: false, pengajuan: null, itemIds: [], itemsList: [] });
      setAlasanRestore('');
      fetchDataPengajuan();
      if (selectedPengajuan && selectedPengajuan.id === restoreModal.pengajuan.id) {
        const resDetail = await http.get('/item-deletions', {
          headers: { Authorization: getAuthorizationHeader() },
        });
        const freshList = resDetail.data?.data?.data || resDetail.data?.data || [];
        const fresh = freshList.find((p) => p.id === restoreModal.pengajuan.id);
        if (fresh) setSelectedPengajuan(fresh);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mengembalikan barang ke etalase');
    } finally {
      setIsSubmittingRestore(false);
    }
  };

  // Resolve Image URL Helper
  const resolveItemImageUrl = (item) => {
    if (!item) return '';
    let gData = item.gambar1 || item.gambar || item.foto;
    if (typeof gData === 'string' && (gData.startsWith('[') || gData.startsWith('{'))) {
      try {
        gData = JSON.parse(gData);
      } catch (e) {}
    }
    if (Array.isArray(gData) && gData.length > 0) {
      const path = gData[0]?.asli || gData[0]?.thumbnail || (typeof gData[0] === 'string' ? gData[0] : '');
      return path ? (path.startsWith('http') ? path : `${imageBaseUrl}${path}`) : '';
    }
    if (typeof gData === 'string' && gData.length > 0) {
      return gData.startsWith('http') ? gData : `${imageBaseUrl}${gData}`;
    }
    return '';
  };

  // Normalized Item List inside Modal
  const modalItemList = useMemo(() => {
    if (!selectedPengajuan || !selectedPengajuan.items) return [];
    return selectedPengajuan.items
      .map((child) => {
        const itemObj = child.item || child;
        if (!itemObj) return null;
        return {
          ...itemObj,
          status_item: child.status_item || (selectedPengajuan.status_pengajuan === 'approved' ? 'deleted' : selectedPengajuan.status_pengajuan),
          restored_at: child.restored_at,
          restored_by: child.restored_by || child.restoredBy,
          alasan_restore: child.alasan_restore,
          raw_child: child,
        };
      })
      .filter(Boolean);
  }, [selectedPengajuan]);

  // Filtered Items inside Modal (Tab List & Tab Galeri)
  const filteredModalItems = useMemo(() => {
    if (!modalItemList.length) return [];
    if (!searchModalItem.trim()) return modalItemList;
    const q = searchModalItem.toLowerCase();
    return modalItemList.filter((it) => {
      const kode = it.kode_item ? it.kode_item.toLowerCase() : '';
      const jenis = it.jenis_kategori?.nama_jenis ? it.jenis_kategori.nama_jenis.toLowerCase() : '';
      const kat = it.jenis_kategori?.kategori?.nama_kategori ? it.jenis_kategori.kategori.nama_kategori.toLowerCase() : '';
      const baki = it.nomor_rak ? it.nomor_rak.toString().toLowerCase() : '';
      return kode.includes(q) || jenis.includes(q) || kat.includes(q) || baki.includes(q);
    });
  }, [modalItemList, searchModalItem]);

  const detailSummary = useMemo(() => {
    if (!selectedPengajuan || !selectedPengajuan.items) return {};
    return selectedPengajuan.items.reduce((acc, child) => {
      const item = child.item || child;
      if (!item) return acc;
      const catName = item.jenis_kategori?.kategori?.nama_kategori || 'Tanpa Kategori';
      const karat = item.karat || '-';
      const berat = parseFloat(item.berat || 0);

      if (!acc[catName]) acc[catName] = { name: catName, pcs: 0, berat: 0, byKarat: {} };
      acc[catName].pcs += 1;
      acc[catName].berat += berat;

      if (!acc[catName].byKarat[karat]) acc[catName].byKarat[karat] = { pcs: 0, berat: 0 };
      acc[catName].byKarat[karat].pcs += 1;
      acc[catName].byKarat[karat].berat += berat;

      return acc;
    }, {});
  }, [selectedPengajuan]);

  const fetchDataPengajuan = useCallback(async (dateOverride) => {
    setLoading(true);
    try {
      const activeStart = dateOverride ? dateOverride.startDate : startDate;
      const activeEnd = dateOverride ? dateOverride.endDate : endDate;
      let url = '/item-deletions';
      if (activeStart && activeEnd) {
        url += `?start_date=${moment(activeStart).format('YYYY-MM-DD')}&end_date=${moment(activeEnd).format('YYYY-MM-DD')}`;
      }
      const res = await http.get(url, {
        headers: { Authorization: getAuthorizationHeader() },
      });
      let result = res.data.data || [];
      if (activeStart && activeEnd) {
        result = result.filter((item) => {
          const date = moment(item.created_at);
          return date.isBetween(moment(activeStart).startOf('day'), moment(activeEnd).endOf('day'), null, '[]');
        });
      }
      setData(result);
    } catch (err) {
      console.log(err);
      toast.error('Gagal mengambil data pengajuan');
    }
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => {
    fetchDataPengajuan();
  }, [fetchDataPengajuan]);

  // Reload / Refresh Default: reset semua filter ke kondisi awal dan muat ulang data default
  const handleReloadDefault = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setShowDatePicker(false);
    setStatusFilter('');
    setCabangFilter('');
    setOperatorFilter('');
    setSearchQuery('');
    setCurrentPage(1);
    fetchDataPengajuan({ startDate: null, endDate: null });
  }, [fetchDataPengajuan]);

  const handlePersetujuan = async (id, action, catatan = '') => {
    setLoading(true);
    try {
      const payload = { action };
      if (catatan) payload.catatan = catatan;
      await http.post(
        `/item-deletions/${id}/persetujuan`,
        payload,
        { headers: { Authorization: getAuthorizationHeader() } }
      );
      toast.success(
        action === 'approve'
          ? 'Pengajuan disetujui, barang berhasil dihapus'
          : action === 'cancel'
          ? 'Pengajuan dibatalkan'
          : 'Pengajuan ditolak, barang dikembalikan ke etalase'
      );
      fetchDataPengajuan();
      window.dispatchEvent(new Event('refresh-pending-verification'));
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || 'Gagal memproses persetujuan');
    }
    setLoading(false);
  };

  function checkBerat(number) {
    const beratConfirm = parseFloat(number || 0);
    return beratConfirm % 1 === 0
      ? beratConfirm.toString()
      : beratConfirm
          .toFixed(2)
          .replace(/\.?0+$/, '')
          .replace('.', ',');
  }

  function showDetailItem(item) {
    setDetailItem(item);
    setOpenDetail(true);
  }

  const handleResetFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setShowDatePicker(false);
    setStatusFilter('');
    setCabangFilter('');
    setOperatorFilter('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Options for Cabang Pemohon (Hanya Toko / is_toko = true & Bukan Brankas)
  const cabangOptions = useMemo(() => {
    const list = new Set();
    cabangList.forEach((c) => {
      const isToko = c.is_toko === true || c.is_toko === 1 || c.is_toko === '1';
      const name = c.nama_cabang || c.nama || (typeof c === 'string' ? c : null);
      if (name && isToko && !name.toLowerCase().includes('brankas')) {
        list.add(name);
      }
    });

    // Fallback jika cabangList belum ada di context
    if (list.size === 0) {
      data.forEach((d) => {
        const cName = d.cabang?.nama_cabang;
        if (cName && !cName.toLowerCase().includes('brankas')) list.add(cName);
      });
    }
    return Array.from(list);
  }, [cabangList, data]);

  const operatorOptions = useMemo(() => {
    const list = new Set();
    data.forEach((d) => {
      const name = d.diajukan_oleh?.name || d.user_pemohon?.name;
      if (name) list.add(name);
    });
    return Array.from(list);
  }, [data]);

  // Filtered Data based on dropdowns & search query
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      if (statusFilter) {
        if (statusFilter === 'approved' && item.status_pengajuan !== 'direct_deleted' && item.status_pengajuan !== 'approved') {
          return false;
        } else if (statusFilter === 'cancelled' && item.status_pengajuan !== 'dibatalkan' && item.status_pengajuan !== 'cancelled') {
          return false;
        } else if (statusFilter !== 'approved' && statusFilter !== 'cancelled' && item.status_pengajuan !== statusFilter) {
          return false;
        }
      }
      if (cabangFilter && item.cabang?.nama_cabang !== cabangFilter) {
        return false;
      }
      if (operatorFilter) {
        const name = item.diajukan_oleh?.name || item.user_pemohon?.name;
        if (name !== operatorFilter) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const batch = item.batch_id ? item.batch_id.toLowerCase() : '';
        const pemohon = item.diajukan_oleh?.name ? item.diajukan_oleh.name.toLowerCase() : '';
        const cabang = item.cabang?.nama_cabang ? item.cabang.nama_cabang.toLowerCase() : '';
        if (!batch.includes(q) && !pemohon.includes(q) && !cabang.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [data, statusFilter, cabangFilter, operatorFilter, searchQuery]);

  // Pagination for Pengajuan Table
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const paginatedData = useMemo(() => {
    return filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleOpenDetailModal = (pengajuan, defaultTab = 'ringkasan') => {
    setSelectedPengajuan(pengajuan);
    setModalTab(defaultTab);
    setSearchModalItem('');
    setIsOpenDetailModal(true);
  };

  const renderStatusBadge = (status, isCompact = false) => {
    if (status === 'pending_approval' || status === 'pending') {
      return (
        <span className={`inline-flex items-center gap-1 font-bold rounded-lg border bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700 ${isCompact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping"></span>
          Menunggu Persetujuan
        </span>
      );
    }
    if (status === 'approved' || status === 'direct_deleted') {
      return (
        <span className={`inline-flex items-center gap-1 font-bold rounded-lg border bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700 ${isCompact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'}`}>
          <IconCheck size={isCompact ? 12 : 14} /> Disetujui
        </span>
      );
    }
    if (status === 'rejected') {
      return (
        <span className={`inline-flex items-center gap-1 font-bold rounded-lg border bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-700 ${isCompact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'}`}>
          <IconX size={isCompact ? 12 : 14} /> Ditolak
        </span>
      );
    }
    if (status === 'cancelled' || status === 'dibatalkan') {
      return (
        <span className={`inline-flex items-center gap-1 font-bold rounded-lg border bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-700 ${isCompact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'}`}>
          <IconX size={isCompact ? 12 : 14} /> Dibatalkan
        </span>
      );
    }
    return <span className="text-xs">{status}</span>;
  };

  const activeFilters = useMemo(() => {
    const list = [];
    if (statusFilter) {
      const statusMap = {
        pending_approval: 'Menunggu Persetujuan',
        approved: 'Disetujui',
        rejected: 'Ditolak',
        cancelled: 'Dibatalkan',
      };
      list.push({
        id: 'status',
        label: `Status: ${statusMap[statusFilter] || statusFilter}`,
        icon: '📋',
        onClear: () => {
          setStatusFilter('');
          setCurrentPage(1);
        },
      });
    }
    if (startDate && endDate) {
      list.push({
        id: 'tanggal',
        label: awal === akhir ? `Tanggal: ${awal}` : `Tanggal: ${awal} - ${akhir}`,
        icon: '📅',
        onClear: () => {
          setStartDate(null);
          setEndDate(null);
          setCurrentPage(1);
        },
      });
    }
    if (cabangFilter) {
      list.push({
        id: 'cabang',
        label: `Cabang Pemohon: ${cabangFilter}`,
        icon: '🏪',
        onClear: () => {
          setCabangFilter('');
          setCurrentPage(1);
        },
      });
    }
    if (operatorFilter) {
      list.push({
        id: 'operator',
        label: `Pemohon: ${operatorFilter}`,
        icon: '👤',
        onClear: () => {
          setOperatorFilter('');
          setCurrentPage(1);
        },
      });
    }
    return list;
  }, [statusFilter, startDate, endDate, awal, akhir, cabangFilter, operatorFilter]);

  const activeFilterCount = activeFilters.length;
  const hasActiveFilters = activeFilterCount > 0 || Boolean(searchQuery);

  return (
    <Layouts 
    title="Pengajuan Hapus Etalase"
    >
      <DetailItem
        checkBerat={checkBerat}
        detailItem={detailItem}
        closeModalDetail={() => setOpenDetail(false)}
        openDetail={openDetail}
      />

      <div className="p-3 sm:p-4 lg:p-6 w-full space-y-4 min-h-[85vh]">
        {/* Top Header Card */}
        <div className="bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm">
          {/* Search & Filter & View Toggle Trigger Bar */}
          <div className="flex items-center gap-2 pt-3">
            <div className="relative flex-1">
              <IconSearch size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Cari No. Request, cabang, pemohon..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-9 py-2 sm:py-2.5 text-xs sm:text-sm bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-100 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5"
                >
                  <IconX size={15} />
                </button>
              )}
            </div>

            {/* View Toggle (Cards vs Table) */}
            <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200/70 dark:border-slate-700 shrink-0">
              <button
                type="button"
                onClick={() => setViewMode('cards')}
                className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${
                  viewMode === 'cards'
                    ? 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
                title="Tampilan Kartu (Rekomendasi HP/Tablet)"
              >
                <IconLayoutGrid size={17} />
                <span className="hidden sm:inline">Kartu</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${
                  viewMode === 'table'
                    ? 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
                title="Tampilan Tabel"
              >
                <IconTable size={17} />
                <span className="hidden sm:inline">Tabel</span>
              </button>
            </div>

            {/* Refresh Button */}
            <button
              type="button"
              onClick={handleReloadDefault}
              disabled={loading}
              className="p-2 sm:py-2.5 text-slate-600 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-400 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-all shrink-0 cursor-pointer"
              title="Reset Semua Filter & Muat Ulang Data"
            >
              <IconReload size={18} className={loading ? 'animate-spin text-amber-500' : ''} />
            </button>

            {/* Filter Toggle Button */}
            <button
              type="button"
              onClick={() => setIsFilterOpen(true)}
              className={`flex items-center gap-1.5 px-3 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold border transition-all shrink-0 cursor-pointer shadow-xs ${
                isFilterOpen || activeFilterCount > 0
                  ? 'bg-amber-500 border-amber-600 text-white shadow-amber-500/20'
                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-amber-400'
              }`}
              title="Buka Filter"
            >
              <IconAdjustmentsHorizontal size={17} />
              <span>Filter</span>
              {activeFilterCount > 0 && (
                <span
                  className={`px-1.5 py-0.2 text-[10px] font-bold rounded-full leading-tight ${
                    isFilterOpen || activeFilterCount > 0
                      ? 'bg-white text-amber-700'
                      : 'bg-amber-500 text-white'
                  }`}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Horizontal Status Chips (Touch friendly pill bar) */}
          <div className="flex items-center gap-1.5 overflow-x-auto pt-2.5 pb-1 scrollbar-none">
            {[
              { label: 'Semua Status', value: '' },
              { label: 'Menunggu Verifikasi', value: 'pending_approval', badgeColor: 'bg-amber-500' },
              { label: 'Disetujui', value: 'approved', badgeColor: 'bg-emerald-500' },
              { label: 'Ditolak', value: 'rejected', badgeColor: 'bg-rose-500' },
              { label: 'Dibatalkan', value: 'cancelled', badgeColor: 'bg-slate-400' },
            ].map((chip) => {
              const isActive = statusFilter === chip.value;
              return (
                <button
                  key={chip.value}
                  type="button"
                  onClick={() => {
                    setStatusFilter(chip.value);
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 text-xs font-bold rounded-xl whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer flex-shrink-0 ${
                    isActive
                      ? 'bg-amber-500 text-white shadow-sm shadow-amber-500/20'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {chip.badgeColor && (
                    <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-white' : chip.badgeColor}`}></span>
                  )}
                  <span>{chip.label}</span>
                </button>
              );
            })}
          </div>

          {/* Active Filter Chips (Pills horizontal bar) */}
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-1.5 overflow-x-auto pt-2 pb-0.5 w-full max-w-full no-scrollbar text-xs">
              <span className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 shrink-0 uppercase tracking-wider">
                Filter:
              </span>
              {activeFilters.map((flt) => (
                <div
                  key={flt.id}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-300 font-medium whitespace-nowrap shadow-2xs shrink-0"
                >
                  <span className="text-xs">{flt.icon}</span>
                  <span className="max-w-[150px] sm:max-w-[200px] truncate">{flt.label}</span>
                  <button
                    type="button"
                    onClick={flt.onClear}
                    className="p-0.5 hover:bg-amber-200/60 dark:hover:bg-amber-800/60 rounded-full transition-colors ml-0.5 cursor-pointer text-amber-600 dark:text-amber-400"
                    title={`Hapus filter ${flt.label}`}
                  >
                    <IconX size={12} />
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={handleResetFilter}
                className="text-[11px] font-bold text-rose-600 dark:text-rose-400 hover:underline px-2 py-0.5 rounded shrink-0 cursor-pointer whitespace-nowrap"
              >
                Reset Semua
              </button>
            </div>
          )}

          {/* ═══ Filter Slide-over Drawer (Muncul dari Kanan Layar) ═══ */}
          <Transition appear show={isFilterOpen} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-[99999]"
              onClose={() => setIsFilterOpen(false)}
            >
              {/* Backdrop Overlay Tipis & Tanpa Blur (Data di samping tetap tajam dan terbaca jelas) */}
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-slate-900/15 dark:bg-black/30 pointer-events-auto" />
              </Transition.Child>

              {/* Drawer Container */}
              <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-10">
                  <Transition.Child
                    as={Fragment}
                    enter="transform transition ease-in-out duration-300 sm:duration-400"
                    enterFrom="translate-x-full"
                    enterTo="translate-x-0"
                    leave="transform transition ease-in-out duration-300 sm:duration-400"
                    leaveFrom="translate-x-0"
                    leaveTo="translate-x-full"
                  >
                    <Dialog.Panel className="pointer-events-auto w-screen max-w-[340px] sm:max-w-[420px] md:max-w-[460px] transform bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 text-left align-middle transition-all flex flex-col h-screen max-h-screen">
                      {/* Drawer Header */}
                      <div className="px-4 py-3.5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between shrink-0 sticky top-0 z-20">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                            <IconAdjustmentsHorizontal size={18} />
                          </div>
                          <div>
                            <Dialog.Title as="h2" className="text-sm font-bold text-slate-800 dark:text-slate-100">
                              Filter Pengajuan
                            </Dialog.Title>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                              {activeFilterCount > 0
                                ? `${activeFilterCount} kriteria aktif`
                                : 'Parameter pencarian'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsFilterOpen(false)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                        >
                          <IconX size={16} />
                        </button>
                      </div>

                      {/* Drawer Body (Scrollable) */}
                      <div className="p-4 flex-1 overflow-y-auto space-y-3.5">
                        {/* 1. Filter Tanggal Pengajuan (Collapsible / Trigger Klik) */}
                        <div>
                          {startDate && (
                            <div className="flex items-center justify-end mb-1.5">
                              <button
                                type="button"
                                onClick={() => {
                                  setStartDate(null);
                                  setEndDate(null);
                                  setShowDatePicker(false);
                                  setCurrentPage(1);
                                }}
                                className="text-[11px] font-bold text-rose-500 hover:underline cursor-pointer"
                              >
                                Reset Tanggal
                              </button>
                            </div>
                          )}

                          {/* Trigger Button untuk membuka kalender */}
                          <button
                            type="button"
                            onClick={() => setShowDatePicker((prev) => !prev)}
                            className={`w-full h-11 border rounded-xl px-3.5 flex items-center justify-between transition-all cursor-pointer text-xs sm:text-sm font-medium ${
                              showDatePicker
                                ? 'border-amber-500 ring-2 ring-amber-500/20 bg-amber-50/40 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300'
                                : startDate && endDate
                                ? 'border-amber-400 bg-amber-50/20 dark:bg-amber-950/10 text-slate-800 dark:text-slate-100'
                                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:border-amber-400'
                            }`}
                          >
                            <div className="flex items-center gap-2 overflow-hidden">
                              <IconCalendar size={18} className="text-amber-500 shrink-0" />
                              <span className="truncate">
                                {startDate && endDate
                                  ? `${moment(startDate).format('DD/MM/YYYY')} - ${moment(endDate).format('DD/MM/YYYY')}`
                                  : 'Pilih Rentang Tanggal'}
                              </span>
                            </div>
                            <span className="text-[11px] text-slate-400 font-semibold shrink-0 ml-2">
                              {showDatePicker ? 'Tutup ▲' : 'Buka ▼'}
                            </span>
                          </button>

                          {/* Kalender DateRange: hanya muncul setelah tombol trigger diklik */}
                          {showDatePicker && (
                            <div className="mt-2.5 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden p-2 flex flex-col items-center bg-white dark:bg-slate-800 shadow-sm animate-in fade-in duration-150">
                              <DateRange
                                ranges={[selectionRange]}
                                onChange={handleSelectDate}
                                rangeColors={['#f59e0b']}
                                months={1}
                                direction="horizontal"
                                showDateDisplay={false}
                                moveRangeOnFirstSelection={false}
                              />
                              <div className="w-full flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700 mt-1 px-1">
                                <span className="text-[11px] text-slate-400">
                                  {startDate && endDate ? `${moment(startDate).format('DD/MM/YY')} - ${moment(endDate).format('DD/MM/YY')}` : 'Pilih rentang tanggal'}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setShowDatePicker(false)}
                                  className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-bold transition cursor-pointer"
                                >
                                  Terapkan
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* 2. Filter Status Pengajuan */}
                        <div>
                          <select
                            value={statusFilter}
                            onChange={(e) => {
                              setStatusFilter(e.target.value);
                              setCurrentPage(1);
                            }}
                            className="w-full h-11 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-xs sm:text-sm text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 cursor-pointer hover:border-amber-400 dark:hover:border-amber-500 outline-none font-medium shadow-xs"
                          >
                            <option value="">Semua Status</option>
                            <option value="pending_approval">Menunggu Persetujuan</option>
                            <option value="approved">Disetujui</option>
                            <option value="rejected">Ditolak</option>
                            <option value="cancelled">Dibatalkan</option>
                          </select>
                        </div>

                        {/* 3. Cabang Pemohon */}
                        <div>
                          <select
                            value={cabangFilter}
                            onChange={(e) => {
                              setCabangFilter(e.target.value);
                              setCurrentPage(1);
                            }}
                            className="w-full h-11 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-xs sm:text-sm text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 cursor-pointer hover:border-amber-400 dark:hover:border-amber-500 outline-none font-medium shadow-xs"
                          >
                            <option value="">Semua Cabang Pemohon</option>
                            {cabangOptions.map((c, i) => (
                              <option key={i} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* 4. Operator Pemohon */}
                        <div>
                          <select
                            value={operatorFilter}
                            onChange={(e) => {
                              setOperatorFilter(e.target.value);
                              setCurrentPage(1);
                            }}
                            className="w-full h-11 border border-slate-200 dark:border-slate-700 rounded-xl px-3 text-xs sm:text-sm text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 cursor-pointer hover:border-amber-400 dark:hover:border-amber-500 outline-none font-medium shadow-xs"
                          >
                            <option value="">Semua Operator</option>
                            {operatorOptions.map((op, i) => (
                              <option key={i} value={op}>
                                {op}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Drawer Footer (Sticky Bottom) */}
                      <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 flex items-center justify-between gap-3 shrink-0">
                        <button
                          type="button"
                          onClick={() => setIsFilterOpen(false)}
                          className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl shadow-sm transition cursor-pointer"
                        >
                          Tutup
                        </button>
                        <div className="flex items-center gap-2">
                          {activeFilterCount > 0 && (
                            <button
                              type="button"
                              onClick={handleResetFilter}
                              className="px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl transition cursor-pointer flex items-center gap-1"
                            >
                              <IconRotateClockwise size={14} />
                              <span>Reset</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>
        </div>

        {/* Content Container: Data Pengajuan */}
        <div>
            {loading ? (
              <div className="py-24 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col items-center justify-center gap-3">
                <span className="loading loading-spinner loading-lg text-amber-500"></span>
                <p className="text-slate-400 text-xs font-semibold animate-pulse">Memuat data pengajuan hapus etalase...</p>
              </div>
            ) : paginatedData.length === 0 ? (
              <div className="py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col items-center justify-center text-center p-6 space-y-3">
                <div className="w-16 h-16 bg-amber-50 dark:bg-slate-800 rounded-full flex items-center justify-center text-amber-500">
                  <IconInbox size={32} />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-slate-800 dark:text-slate-200">
                    Tidak Ada Pengajuan Ditemukan
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mt-1">
                    {hasActiveFilters
                      ? 'Tidak ada riwayat pengajuan hapus etalase yang sesuai dengan filter atau kata kunci Anda.'
                      : 'Belum ada data pengajuan hapus etalase yang tersimpan.'}
                  </p>
                </div>
                <div className="flex items-center gap-2 pt-1 flex-wrap justify-center">
                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={handleResetFilter}
                      className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl shadow-sm transition-all cursor-pointer"
                    >
                      Reset Semua Filter
                    </button>
                  )}
                </div>
              </div>
            ) : viewMode === 'cards' ? (
              /* CARD FEED VIEW FOR PENGAJUAN */
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 sm:gap-4">
                  {paginatedData.map((pengajuan) => {
                    const totalPcs = pengajuan.items?.length || 0;
                    const restoredCount = (pengajuan.items || []).filter((c) => c.status_item === 'restored').length;
                    const totalBerat =
                      pengajuan.items?.reduce(
                        (total, child) => total + parseFloat((child.item || child)?.berat || 0),
                        0
                      ) || 0;
                    const displayBerat = parseFloat(totalBerat).toFixed(3);
                    const isPending =
                      pengajuan.status_pengajuan === 'pending_approval' || pengajuan.status_pengajuan === 'pending';

                    return (
                      <div
                        key={pengajuan.id}
                        className={`bg-white dark:bg-slate-900 rounded-2xl p-4 sm:p-5 border transition-all duration-200 flex flex-col justify-between shadow-sm hover:shadow-md ${
                          isPending
                            ? 'border-amber-300/80 dark:border-amber-700/60 ring-1 ring-amber-400/20'
                            : 'border-slate-200/80 dark:border-slate-800'
                        }`}
                      >
                        {/* Top: No Request, Status, Date */}
                        <div>
                          <div className="flex items-start justify-between gap-2 pb-2.5 border-b border-slate-100 dark:border-slate-800">
                            <div>
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono font-extrabold text-sm sm:text-base text-slate-900 dark:text-slate-100 tracking-tight">
                                  {pengajuan.batch_id}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => handleCopyBatchId(e, pengajuan.batch_id)}
                                  className="text-slate-400 hover:text-amber-500 p-0.5 rounded transition-colors cursor-pointer"
                                  title="Salin No. Request"
                                >
                                  <IconCopy size={14} />
                                </button>
                              </div>
                              <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 flex items-center gap-1">
                                <IconClock size={13} />
                                <span>{moment(pengajuan.created_at).format('DD MMM YYYY, HH.mm')}</span>
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              {renderStatusBadge(pengajuan.status_pengajuan, true)}
                              {restoredCount > 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">
                                  <IconRotateClockwise size={11} />
                                  {restoredCount === totalPcs ? 'Semua Dikembalikan' : `${restoredCount}/${totalPcs} Dikembalikan`}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Body: Cabang, Pemohon, Verifikator, Metrics */}
                          <div className="py-3 space-y-2 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                <span>Cabang Pemohon:</span>
                              </span>
                              <span className="font-bold text-slate-800 dark:text-slate-200 text-right">
                                {pengajuan.cabang?.nama_cabang || '-'}
                              </span>
                            </div>

                            <div className="flex items-center justify-between">
                              <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                <span>Pemohon:</span>
                              </span>
                              <span className="font-semibold text-slate-700 dark:text-slate-300 text-right">
                                {pengajuan.diajukan_oleh?.name || pengajuan.user_pemohon?.name || 'Sistem'}
                              </span>
                            </div>

                            {pengajuan.disetujui_oleh?.name && (
                              <div className="flex items-center justify-between">
                                <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                  <span>Verifikator:</span>
                                </span>
                                <span className="font-semibold text-emerald-600 dark:text-emerald-400 text-right">
                                  {pengajuan.disetujui_oleh.name}
                                </span>
                              </div>
                            )}

                            {/* Highlight Metrics Box */}
                            <div className="mt-2.5 p-3 rounded-xl bg-gradient-to-r from-amber-50/90 to-amber-100/60 dark:from-amber-950/30 dark:to-slate-800/80 border border-amber-200/70 dark:border-amber-900/40 flex items-center justify-between">
                              <div>
                                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-400 block">
                                  Total Barang
                                </span>
                                <span className="text-base font-extrabold text-slate-900 dark:text-slate-100">
                                  {totalPcs} <span className="text-xs font-semibold text-slate-500">PCS</span>
                                </span>
                              </div>
                              <div className="text-right">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-400 block">
                                  Total Berat
                                </span>
                                <span className="text-base font-extrabold text-amber-600 dark:text-amber-400 font-mono">
                                  {displayBerat} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Footer Actions (Thumb Friendly) */}
                        <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
                          {isPending && canVerify && (
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() => confirmAction(pengajuan.id, 'approve')}
                                className="w-full py-2.5 px-3 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-bold rounded-xl shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                              >
                                <IconCheck size={16} />
                                <span>Terima</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => confirmAction(pengajuan.id, 'reject')}
                                className="w-full py-2.5 px-3 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-xs font-bold rounded-xl shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                              >
                                <IconX size={16} />
                                <span>Tolak</span>
                              </button>
                            </div>
                          )}

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenDetailModal(pengajuan, 'ringkasan')}
                              className="flex-1 py-2 px-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                            >
                              <IconEye size={15} />
                              <span>Detail Item</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenDetailModal(pengajuan, 'galeri')}
                              className="py-2 px-3 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/30 dark:hover:bg-amber-900/40 text-amber-700 dark:text-amber-400 text-xs font-semibold rounded-xl border border-amber-200 dark:border-amber-800 transition-all flex items-center justify-center gap-1 cursor-pointer"
                              title="Lihat Galeri Foto"
                            >
                              <IconPhoto size={15} />
                              <span className="hidden sm:inline">Foto</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* TABLE VIEW FOR PENGAJUAN */
              <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm overflow-x-auto">
                <table className="w-full text-xs text-left text-slate-700 dark:text-slate-300">
                  <thead className="bg-amber-50 dark:bg-slate-800 text-amber-800 dark:text-amber-300 border-b border-amber-200/70 dark:border-slate-700 text-[11px] font-bold uppercase tracking-wider">
                    <tr>
                      <th className="p-3.5">No.</th>
                      <th className="p-3.5">Tanggal</th>
                      <th className="p-3.5">Toko</th>
                      <th className="p-3.5">Pemohon</th>
                      <th className="p-3.5">Verifikator</th>
                      <th className="p-3.5 text-center">Total Barang</th>
                      <th className="p-3.5">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {paginatedData.map((pengajuan, idx) => {
                      const totalPcs = pengajuan.items?.length || 0;
                      const restoredCount = (pengajuan.items || []).filter((c) => c.status_item === 'restored').length;
                      const totalBerat =
                        pengajuan.items?.reduce(
                          (total, child) => total + parseFloat((child.item || child)?.berat || 0),
                          0
                        ) || 0;

                      return (
                        <tr
                          key={pengajuan.id}
                          onClick={() => handleOpenDetailModal(pengajuan, 'ringkasan')}
                          className="hover:bg-amber-50/30 dark:hover:bg-slate-800/40 transition-colors cursor-pointer"
                        >
                          <td className="p-3.5 text-slate-500 font-medium">{(currentPage - 1) * itemsPerPage + idx + 1}.</td>
                          <td className="p-3.5 text-slate-500">{moment(pengajuan.created_at).format('DD MMM YYYY, HH.mm')}</td>
                          <td className="p-3.5 font-semibold text-slate-800 dark:text-slate-200">{pengajuan.cabang?.nama_cabang || '-'}</td>
                          <td className="p-3.5">{pengajuan.diajukan_oleh?.name || pengajuan.user_pemohon?.name || 'Sistem'}</td>
                          <td className="p-3.5 font-semibold text-emerald-600">{pengajuan.disetujui_oleh?.name || '-'}</td>
                          <td className="p-3.5 text-center font-bold text-slate-800 dark:text-slate-100">
                            {totalPcs} PCS ({parseFloat(totalBerat).toFixed(3)} gr)
                          </td>
                          <td className="p-3.5">
                            <div className="flex flex-col gap-1 items-start">
                              {renderStatusBadge(pengajuan.status_pengajuan, true)}
                              {restoredCount > 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">
                                  <IconRotateClockwise size={11} />
                                  {restoredCount === totalPcs ? 'Semua Dikembalikan' : `${restoredCount}/${totalPcs} Dikembalikan`}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls for Pengajuan */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm mt-4">
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  Menampilkan {(currentPage - 1) * itemsPerPage + 1} -{' '}
                  {Math.min(currentPage * itemsPerPage, filteredData.length)} dari {filteredData.length} pengajuan
                </span>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 disabled:opacity-40 cursor-pointer"
                  >
                    Prev
                  </button>
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-200 px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 disabled:opacity-40 cursor-pointer"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>



        {/* ========================================================================= */}
        {/* DETAIL SLIDE-OVER DRAWER (5 TABS, MUNCUL DARI KANAN LAYAR)                */}
        {/* ========================================================================= */}
        <Transition appear show={isOpenDetailModal && Boolean(selectedPengajuan)} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-[9999]"
            onClose={() => setIsOpenDetailModal(false)}
          >
            {/* Backdrop Overlay Tipis & Tanpa Blur (Data di samping tetap tajam dan terbaca jelas) */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-slate-900/15 dark:bg-black/30 pointer-events-auto" />
            </Transition.Child>

            {/* Drawer Container */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
              <div className="fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-10">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300 sm:duration-400"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300 sm:duration-400"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl xl:max-w-5xl transform bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 text-left align-middle transition-all flex flex-col h-screen max-h-screen">
                    {selectedPengajuan && (() => {
                      const userPemohon =
                        selectedPengajuan.diajukan_oleh ||
                        selectedPengajuan.user_pemohon ||
                        selectedPengajuan.userPemohon ||
                        selectedPengajuan.user;
                      const userVerifikator =
                        selectedPengajuan.disetujui_oleh ||
                        selectedPengajuan.user_verifikator ||
                        selectedPengajuan.userVerifikator;
                      const totalPcs = selectedPengajuan.items?.length || 0;
                      const totalBerat =
                        selectedPengajuan.items?.reduce(
                          (total, child) => total + parseFloat((child.item || child)?.berat || 0),
                          0
                        ) || 0;
                      const isPending =
                        selectedPengajuan.status_pengajuan === 'pending_approval' || selectedPengajuan.status_pengajuan === 'pending';

                      return (
                        <>
                          {/* Modal Header */}
                          <div className="p-4 sm:p-5 pb-3 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between gap-2 flex-shrink-0 bg-white dark:bg-slate-900 sticky top-0 z-20">
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Dialog.Title as="h2" className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100 font-mono">
                                  {selectedPengajuan.batch_id}
                                </Dialog.Title>
                                {renderStatusBadge(selectedPengajuan.status_pengajuan, true)}
                              </div>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                {selectedPengajuan.cabang?.nama_cabang || '-'} • Oleh{' '}
                                <strong className="text-slate-700 dark:text-slate-300">{userPemohon?.name || 'Sistem'}</strong>
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() => setIsOpenDetailModal(false)}
                              className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                            >
                              <IconX size={20} />
                            </button>
                          </div>

                {/* Tab Navigation (Segmented touch bar) */}
                <div className="flex border-b border-slate-200 dark:border-slate-800 gap-1 px-4 pt-1 overflow-x-auto scrollbar-none flex-shrink-0 bg-slate-50/50 dark:bg-slate-900/50">
                  {[
                    { key: 'ringkasan', label: 'Ringkasan', icon: IconChartPie },
                    { key: 'list', label: `Daftar Item (${selectedPengajuan.items?.length || 0})`, icon: IconListDetails },
                    { key: 'operator', label: 'Pemohon', icon: IconUser },
                    { key: 'verifikator', label: 'Verifikator', icon: IconCircleCheck },
                    { key: 'galeri', label: 'Galeri Foto', icon: IconPhoto },
                  ].map((tab) => {
                    const Icon = tab.icon;
                    const isActive = modalTab === tab.key;
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setModalTab(tab.key)}
                        className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                          isActive
                            ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/60 dark:bg-amber-950/20'
                            : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                        }`}
                      >
                        <Icon size={15} />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Scrollable Tab Content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
                  {/* 1. Tab Ringkasan */}
                  {modalTab === 'ringkasan' && (() => {
                    const itemsList = selectedPengajuan.items || [];
                    const totalPcsVal = totalPcs || itemsList.length;
                    const totalBeratVal = parseFloat(totalBerat || itemsList.reduce((acc, it) => acc + parseFloat((it.item || it)?.berat || 0), 0));
                    const avgBeratVal = itemsList.length > 0 ? (totalBeratVal / itemsList.length) : (totalPcsVal > 0 ? totalBeratVal / totalPcsVal : 0);
                    const validModalItems = itemsList.filter(it => Number((it.item || it)?.harga_modal || 0) > 0);
                    const avgModalVal = validModalItems.length > 0
                      ? Math.round(validModalItems.reduce((acc, it) => acc + Number((it.item || it)?.harga_modal || 0), 0) / validModalItems.length)
                      : 0;
                    const maxBeratVal = itemsList.length > 0 ? Math.max(...itemsList.map(it => parseFloat((it.item || it)?.berat || 0) || 0)) : 0;
                    const maxModalVal = itemsList.length > 0 ? Math.max(...itemsList.map(it => Number((it.item || it)?.harga_modal || 0) || 0)) : 0;

                    return (
                      <div className="space-y-4">
                        {/* Ringkasan Cards (7 Cards) */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3">
                          <div className="bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                            <span className="text-amber-800 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">Total Barang</span>
                            <div className="text-lg font-extrabold text-slate-900 dark:text-slate-100 mt-0.5">
                              {totalPcsVal} <span className="text-xs font-semibold text-slate-500">Pcs</span>
                            </div>
                          </div>

                          <div className="bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                            <span className="text-amber-800 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">Total Berat</span>
                            <div className="text-lg font-extrabold text-amber-600 dark:text-amber-400 mt-0.5 font-mono">
                              {totalBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                            </div>
                          </div>

                          <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Total Kategori</span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                              {Object.keys(detailSummary).length} <span className="text-xs font-semibold text-slate-500">Kategori</span>
                            </div>
                          </div>

                          <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Rata-rata Berat</span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              {avgBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                            </div>
                          </div>

                          <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Rata-rata Modal</span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              Rp {avgModalVal.toLocaleString('id-ID')}
                            </div>
                          </div>

                          <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Berat Maksimal</span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              {maxBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                            </div>
                          </div>

                          <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Modal Maksimal</span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              Rp {maxModalVal.toLocaleString('id-ID')}
                            </div>
                          </div>
                        </div>

                      {/* Breakdown Kategori & Karat */}
                      {Object.keys(detailSummary).length > 0 && (
                        <div className="space-y-3 p-3.5 bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-900/30 rounded-xl">
                          <h4 className="text-xs font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                            <IconChartPie size={16} />
                            <span>Breakdown Kategori & Karat</span>
                          </h4>
                          <div className="space-y-2">
                            {Object.values(detailSummary).map((cat, idx) => (
                              <div key={idx} className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-amber-200/50 dark:border-slate-700 shadow-sm">
                                <div className="flex justify-between items-center mb-2 pb-2 border-b border-slate-100 dark:border-slate-700">
                                  <span className="font-bold text-slate-800 dark:text-slate-100 text-sm">{cat.name}</span>
                                  <span className="font-bold text-amber-600 dark:text-amber-400 text-xs bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-2.5 py-0.5 rounded-md">
                                    {cat.pcs} Pcs ({cat.berat.toFixed(3)} gr)
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                  {Object.entries(cat.byKarat).map(([karat, val], i) => (
                                    <div key={i} className="flex items-center justify-between bg-slate-50 dark:bg-slate-700/40 px-2.5 py-1.5 rounded-lg text-xs border border-slate-100 dark:border-slate-700">
                                      <span className="font-semibold text-slate-500 dark:text-slate-400">{karat} K</span>
                                      <span className="font-bold text-slate-800 dark:text-slate-100">{val.pcs} Pcs / {val.berat.toFixed(3)}g</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Catatan Area */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="p-3.5 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
                          <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">Alasan Pengajuan Hapus</span>
                          <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                            {selectedPengajuan.alasan || selectedPengajuan.keterangan || <span className="italic text-slate-400">Tidak ada catatan</span>}
                          </p>
                        </div>
                        <div className="p-3.5 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
                          <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">Catatan Verifikator</span>
                          <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                            {selectedPengajuan.catatan || (
                              <span className="italic text-slate-400">
                                {isPending ? 'Menunggu verifikasi...' : 'Tidak ada catatan verifikator'}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                  {/* 2. Tab Daftar Item */}
                  {modalTab === 'list' && (() => {
                    const isApprovedPengajuan =
                      selectedPengajuan.status_pengajuan === 'approved' ||
                      selectedPengajuan.status_pengajuan === 'direct_deleted';
                    const unRestoredItems = modalItemList.filter((it) => it.status_item !== 'restored');

                    return (
                      <div className="space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="relative flex-1 max-w-xs">
                            <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                            <input
                              type="text"
                              placeholder="Cari kode, jenis, baki..."
                              value={searchModalItem}
                              onChange={(e) => setSearchModalItem(e.target.value)}
                              className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:ring-1 focus:ring-amber-500"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500 font-semibold">
                              {filteredModalItems.length} item
                            </span>
                            {isApprovedPengajuan && canVerify && unRestoredItems.length > 0 && (
                              <button
                                type="button"
                                onClick={() => handleOpenRestoreModal(selectedPengajuan, [])}
                                className="px-3 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer shrink-0"
                                title="Kembalikan semua barang terhapus di pengajuan ini ke etalase toko"
                              >
                                <IconRotateClockwise size={14} />
                                <span>Kembalikan Semua ({unRestoredItems.length})</span>
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Mobile Cards */}
                        <div className="sm:hidden space-y-2">
                          {filteredModalItems.length === 0 ? (
                            <p className="py-8 text-center text-xs text-slate-400">Tidak ada item ditemukan</p>
                          ) : (
                            filteredModalItems.map((it) => {
                              const isRestored = it.status_item === 'restored';
                              return (
                                <div key={it.id || it.kode_item} className="p-3 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-200/80 dark:border-slate-700 flex flex-col gap-2">
                                  <div className="flex items-start justify-between gap-2">
                                    <div>
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-xs font-mono font-bold text-amber-600 dark:text-amber-400">{it.kode_item}</span>
                                        <span className="text-[10px] px-1.5 py-0.2 font-bold bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 rounded">
                                          {it.karat} K
                                        </span>
                                      </div>
                                      <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 mt-0.5">
                                        {it.jenis_kategori?.nama_jenis || '-'}
                                      </p>
                                      <p className="text-[10px] text-slate-400">
                                        Baki/Rak: {it.nomor_rak || '-'}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <span className="text-xs font-bold text-slate-900 dark:text-slate-100 font-mono">
                                        {parseFloat(it.berat || 0).toFixed(3)} gr
                                      </span>
                                    </div>
                                  </div>

                                  <div className="pt-2 border-t border-slate-200/60 dark:border-slate-700/60 flex items-center justify-between gap-2">
                                    {isRestored ? (
                                      <div>
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">
                                          <IconCheck size={11} stroke={3} /> Dikembalikan ke Etalase
                                        </span>
                                        {it.restored_by?.name && (
                                          <span className="block text-[10px] text-slate-400 mt-0.5">
                                            Oleh {it.restored_by.name} {it.restored_at ? `(${moment(it.restored_at).format('DD/MM/YY')})` : ''}
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-300 dark:border-rose-700">
                                        Terhapus
                                      </span>
                                    )}

                                    {isApprovedPengajuan && canVerify && !isRestored && (
                                      <button
                                        type="button"
                                        onClick={() => handleOpenRestoreModal(selectedPengajuan, [it.id || it.raw_child?.item_id])}
                                        className="px-2.5 py-1 text-xs font-bold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 rounded-lg border border-emerald-300 dark:border-emerald-700 flex items-center gap-1 cursor-pointer"
                                      >
                                        <IconRotateClockwise size={13} />
                                        <span>Kembalikan</span>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>

                        {/* Tablet / Desktop Table */}
                        <div className="hidden sm:block border border-slate-200 dark:border-slate-700 rounded-xl overflow-x-auto max-h-80">
                          <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
                            <thead className="bg-amber-50 dark:bg-slate-800 text-[11px] text-amber-800 dark:text-amber-300 uppercase border-b border-amber-200 dark:border-slate-700 sticky top-0 z-10 font-bold">
                              <tr>
                                <th className="p-2.5 w-10 text-center">No</th>
                                <th className="p-2.5">Kode Barang</th>
                                <th className="p-2.5">Nama Barang</th>
                                <th className="p-2.5 text-center">Baki / Rak</th>
                                <th className="p-2.5 text-right">Berat (gr)</th>
                                <th className="p-2.5 text-center">Karat</th>
                                <th className="p-2.5 text-center">Status Barang</th>
                                <th className="p-2.5 text-center">Aksi</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                              {filteredModalItems.length === 0 ? (
                                <tr>
                                  <td colSpan={8} className="text-center py-6 text-slate-400">
                                    Tidak ada item yang sesuai dengan pencarian
                                  </td>
                                </tr>
                              ) : (
                                filteredModalItems.map((it, idx) => {
                                  const isRestored = it.status_item === 'restored';
                                  return (
                                    <tr key={it.id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                                      <td className="p-2.5 text-slate-500 font-medium text-center">{idx + 1}.</td>
                                      <td className="p-2.5 font-mono font-bold text-amber-600 dark:text-amber-400">{it.kode_item}</td>
                                      <td className="p-2.5">{it.jenis_kategori?.nama_jenis || '-'}</td>
                                      <td className="p-2.5 text-center font-medium">{it.nomor_rak || '-'}</td>
                                      <td className="p-2.5 text-right font-bold text-slate-800 dark:text-slate-100">{parseFloat(it.berat).toFixed(3)} gr</td>
                                      <td className="p-2.5 text-center font-semibold">{it.karat} K</td>
                                      <td className="p-2.5 text-center">
                                        {isRestored ? (
                                          <div className="space-y-0.5">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">
                                              <IconCheck size={11} stroke={3} /> Dikembalikan
                                            </span>
                                            <span className="block text-[10px] text-slate-400">
                                              oleh {it.restored_by?.name || 'Admin'}
                                            </span>
                                            {it.alasan_restore && (
                                              <span className="block text-[10px] text-slate-500 italic truncate max-w-[140px] mx-auto" title={it.alasan_restore}>
                                                "{it.alasan_restore}"
                                              </span>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-300 dark:border-rose-700">
                                            Terhapus
                                          </span>
                                        )}
                                      </td>
                                      <td className="p-2.5 text-center">
                                        {isRestored ? (
                                          <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 flex items-center justify-center gap-1">
                                            <IconCheck size={14} /> Di Etalase
                                          </span>
                                        ) : isApprovedPengajuan && canVerify ? (
                                          <button
                                            type="button"
                                            onClick={() => handleOpenRestoreModal(selectedPengajuan, [it.id || it.raw_child?.item_id])}
                                            className="px-2.5 py-1 text-xs font-bold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 rounded-lg border border-emerald-300 dark:border-emerald-700 flex items-center justify-center gap-1 transition-all cursor-pointer shadow-2xs mx-auto"
                                            title="Kembalikan barang ini ke etalase toko"
                                          >
                                            <IconRotateClockwise size={13} />
                                            <span>Kembalikan</span>
                                          </button>
                                        ) : (
                                          <span className="text-slate-400 text-xs">-</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {/* 3. Tab Operator */}
                  {modalTab === 'operator' && (
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-3 text-xs">
                      <div className="flex items-center gap-3 pb-3 border-b border-slate-200 dark:border-slate-700">
                        <div className="w-10 h-10 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center font-bold text-base">
                          {userPemohon?.name ? userPemohon.name.charAt(0).toUpperCase() : <IconUser size={20} />}
                        </div>
                        <div>
                          <h4 className="font-bold text-sm text-slate-800 dark:text-slate-100">
                            {userPemohon?.name || 'Sistem'}
                          </h4>
                          {userPemohon?.email && (
                            <p className="text-[11px] text-slate-400">
                              {userPemohon.email}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                        <div>
                          <span className="text-slate-400 text-[11px] block">Cabang Toko</span>
                          <p className="font-bold text-slate-800 dark:text-slate-200">
                            {selectedPengajuan.cabang?.nama_cabang || '-'}
                          </p>
                        </div>
                        <div>
                          <span className="text-slate-400 text-[11px] block">Waktu Pengajuan</span>
                          <p className="font-semibold text-slate-700 dark:text-slate-300">
                            {moment(selectedPengajuan.created_at).format('DD MMM YYYY, HH:mm')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 4. Tab Verifikator */}
                  {modalTab === 'verifikator' && (
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-3 text-xs">
                      {userVerifikator ? (
                        <>
                          <div className="flex items-center gap-3 pb-3 border-b border-slate-200 dark:border-slate-700">
                            <div className="w-10 h-10 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-bold text-base">
                              <IconCircleCheck size={20} />
                            </div>
                            <div>
                              <h4 className="font-bold text-sm text-slate-800 dark:text-slate-100">
                                {userVerifikator.name}
                              </h4>
                              {userVerifikator?.email && (
                                <p className="text-[11px] text-slate-400">
                                  {userVerifikator.email}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                            <div>
                              <span className="text-slate-400 text-[11px] block">Waktu Selesai Verifikasi</span>
                              <p className="font-semibold text-slate-700 dark:text-slate-300">
                                {moment(selectedPengajuan.updated_at).format('DD MMM YYYY, HH:mm:ss')}
                              </p>
                            </div>
                            <div>
                              <span className="text-slate-400 text-[11px] block">Catatan Verifikator</span>
                              <p className="font-medium text-slate-700 dark:text-slate-300">
                                {selectedPengajuan.catatan || '-'}
                              </p>
                            </div>
                          </div>
                        </>
                      ) : (
                        <p className="text-center py-6 text-amber-600 font-semibold">Sedang dalam antrean menunggu verifikasi.</p>
                      )}
                    </div>
                  )}

                  {/* 5. Tab Galeri */}
                  {modalTab === 'galeri' && (
                    <PhotoProvider>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {filteredModalItems.map((item) => {
                          const imgUrl = resolveItemImageUrl(item);
                          return (
                            <div key={item.id} className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col">
                              <div className="aspect-square bg-slate-100 dark:bg-slate-800 relative flex items-center justify-center overflow-hidden">
                                {imgUrl ? (
                                  <PhotoView src={imgUrl}>
                                    <img
                                      src={imgUrl}
                                      alt={item.kode_item}
                                      className="w-full h-full object-cover cursor-pointer"
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  </PhotoView>
                                ) : (
                                  <div className="text-center text-slate-400 p-2">
                                    <IconPhoto size={24} className="mx-auto opacity-40 mb-1" />
                                    <span className="text-[10px]">No Photo</span>
                                  </div>
                                )}
                                <div className="absolute top-1.5 left-1.5 bg-slate-900/80 text-amber-400 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded">
                                  {item.kode_item}
                                </div>
                              </div>
                              <div className="p-2 text-xs space-y-1">
                                <p className="font-bold text-slate-800 dark:text-slate-200 text-[11px] truncate">
                                  {item.jenis_kategori?.nama_jenis || '-'}
                                </p>
                                <div className="flex justify-between items-center text-[11px] pt-1 border-t border-slate-100 dark:border-slate-700">
                                  <span className="font-bold text-amber-600">{item.karat} K</span>
                                  <span className="font-semibold text-slate-700 dark:text-slate-300">{parseFloat(item.berat || 0).toFixed(3)}g</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </PhotoProvider>
                  )}
                </div>

                {/* Modal Footer (Sticky Action for Mobile) */}
                <div className="p-4 sm:p-5 pt-3 border-t border-slate-100 dark:border-slate-800 flex flex-col-reverse sm:flex-row items-center justify-between gap-2 flex-shrink-0 bg-white dark:bg-slate-900">
                  <button
                    type="button"
                    onClick={() => setIsOpenDetailModal(false)}
                    className="w-full sm:w-auto px-4 py-2.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all cursor-pointer"
                  >
                    Tutup
                  </button>

                  {isPending && canVerify && (
                    <div className="grid grid-cols-2 gap-2 w-full sm:w-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setIsOpenDetailModal(false);
                          confirmAction(selectedPengajuan.id, 'approve');
                        }}
                        className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs rounded-xl shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
                      >
                        <IconCheck size={16} />
                        <span>Terima</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsOpenDetailModal(false);
                          confirmAction(selectedPengajuan.id, 'reject');
                        }}
                        className="px-5 py-2.5 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white font-bold text-xs rounded-xl shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
                      >
                        <IconX size={16} />
                        <span>Tolak</span>
                      </button>
                    </div>
                  )}
                  </div>
                </>
              );
            })()}
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </div>
  </Dialog>
</Transition>

        {/* ========================================================================= */}
        {/* MODAL KONFIRMASI (TERIMA / TOLAK)                                         */}
        {/* ========================================================================= */}
        {confirmModal.isOpen && (
          <div className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl p-5 sm:p-6 shadow-2xl space-y-4 border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-150">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                <h3 className="font-bold text-sm text-slate-800 dark:text-slate-100 flex items-center gap-2">
                  {confirmModal.action === 'approve' ? (
                    <span className="p-1 bg-emerald-100 dark:bg-emerald-950 text-emerald-600 rounded-lg">
                      <IconCheck size={18} />
                    </span>
                  ) : (
                    <span className="p-1 bg-rose-100 dark:bg-rose-950 text-rose-600 rounded-lg">
                      <IconX size={18} />
                    </span>
                  )}
                  <span>
                    {confirmModal.action === 'approve'
                      ? 'Konfirmasi Persetujuan Hapus'
                      : 'Konfirmasi Penolakan Hapus'}
                  </span>
                </h3>
                <button
                  type="button"
                  onClick={() => setConfirmModal({ isOpen: false, id: null, action: null, pengajuan: null })}
                  className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
                >
                  <IconX size={18} />
                </button>
              </div>

              {confirmModal.pengajuan && (
                <div className="p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl text-xs space-y-1.5 border border-slate-200/80 dark:border-slate-700">
                  <div className="flex justify-between">
                    <span className="text-slate-500">No. Request:</span>
                    <span className="font-mono font-bold text-amber-600 dark:text-amber-400">
                      {confirmModal.pengajuan.batch_id}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Cabang:</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {confirmModal.pengajuan.cabang?.nama_cabang || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Total Barang:</span>
                    <span className="font-bold text-slate-800 dark:text-slate-200">
                      {confirmModal.pengajuan.items?.length || 0} PCS (
                      {parseFloat(
                        confirmModal.pengajuan.items?.reduce(
                          (acc, c) => acc + parseFloat((c.item || c)?.berat || 0),
                          0
                        ) || 0
                      ).toFixed(3)}{' '}
                      gr)
                    </span>
                  </div>
                </div>
              )}

              <form onSubmit={processConfirmAction} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                    Catatan Verifikasi{' '}
                    {confirmModal.action === 'reject' && (
                      <span className="text-rose-500">* (Wajib diisi jika menolak)</span>
                    )}
                  </label>
                  <textarea
                    rows={3}
                    placeholder={
                      confirmModal.action === 'approve'
                        ? 'Catatan verifikasi opsional...'
                        : 'Jelaskan alasan penolakan penghapusan barang...'
                    }
                    value={catatanVerifikasi}
                    onChange={(e) => setCatatanVerifikasi(e.target.value)}
                    required={confirmModal.action === 'reject'}
                    className="w-full p-3 text-xs border border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={() => setConfirmModal({ isOpen: false, id: null, action: null, pengajuan: null })}
                    className="px-4 py-2.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl cursor-pointer"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-5 py-2.5 text-xs font-bold text-white rounded-xl shadow-md transition-all flex items-center gap-1.5 cursor-pointer ${
                      confirmModal.action === 'approve'
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'bg-rose-600 hover:bg-rose-700'
                    }`}
                  >
                    {loading
                      ? 'Memproses...'
                      : confirmModal.action === 'approve'
                      ? 'Setujui Hapus'
                      : 'Tolak Hapus'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* MODAL KONFIRMASI PENGEMBALIAN BARANG KE ETALASE                          */}
        {/* ========================================================================= */}
        {restoreModal.isOpen && (
          <div className="fixed inset-0 z-[99999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-2xl p-5 sm:p-6 shadow-2xl space-y-4 border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-150">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                <h3 className="font-bold text-sm text-slate-800 dark:text-slate-100 flex items-center gap-2">
                  <span className="p-1.5 bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 rounded-lg">
                    <IconRotateClockwise size={18} />
                  </span>
                  <span>Kembalikan Barang ke Etalase</span>
                </h3>
                <button
                  type="button"
                  onClick={() => setRestoreModal({ isOpen: false, pengajuan: null, itemIds: [], itemsList: [] })}
                  className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
                >
                  <IconX size={18} />
                </button>
              </div>

              {/* Info Pengajuan & Target Item */}
              <div className="p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl text-xs space-y-2 border border-slate-200/80 dark:border-slate-700">
                <div className="flex justify-between">
                  <span className="text-slate-500">No. Pengajuan:</span>
                  <span className="font-mono font-bold text-amber-600 dark:text-amber-400">
                    {restoreModal.pengajuan?.batch_id}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Cabang:</span>
                  <span className="font-semibold text-slate-800 dark:text-slate-200">
                    {restoreModal.pengajuan?.cabang?.nama_cabang || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Jumlah Barang Dikembalikan:</span>
                  <span className="font-bold text-emerald-600 dark:text-emerald-400">
                    {restoreModal.itemsList.length} item ({parseFloat(
                      restoreModal.itemsList.reduce((acc, it) => acc + parseFloat(it.berat || 0), 0)
                    ).toFixed(3)} gr)
                  </span>
                </div>

                {/* Ringkasan item yang akan dikembalikan */}
                <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                  <span className="text-[11px] font-semibold text-slate-500 block mb-1">
                    Daftar Item:
                  </span>
                  <div className="max-h-28 overflow-y-auto space-y-1 pr-1">
                    {restoreModal.itemsList.map((it, idx) => (
                      <div
                        key={it.id || idx}
                        className="flex items-center justify-between text-[11px] bg-white dark:bg-slate-900 px-2 py-1 rounded border border-slate-200/60 dark:border-slate-800"
                      >
                        <span className="font-mono font-bold text-amber-600 dark:text-amber-400">
                          {it.kode_item}
                        </span>
                        <span className="truncate max-w-[180px] text-slate-700 dark:text-slate-300">
                          {it.jenis_kategori?.nama_jenis || it.nama_barang || '-'}
                        </span>
                        <span className="font-mono text-slate-600 dark:text-slate-400 font-semibold">
                          {parseFloat(it.berat || 0).toFixed(3)} gr
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 rounded-xl text-xs text-amber-800 dark:text-amber-300">
                <p className="font-semibold">Catatan Audit & Sistem:</p>
                <p className="text-[11px] mt-0.5 opacity-90">
                  Barang akan diaktifkan kembali ke etalase toko (status: ready). Catatan riwayat pengajuan hapus tetap disimpan untuk audit lengkap.
                </p>
              </div>

              <form onSubmit={handleProcessRestore} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                    Alasan Pengembalian <span className="text-rose-500">* (Wajib diisi)</span>
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Contoh: Pembatalan hapus karena kesalahan pilih barang oleh operator..."
                    value={alasanRestore}
                    onChange={(e) => setAlasanRestore(e.target.value)}
                    required
                    className="w-full p-3 text-xs border border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={() => setRestoreModal({ isOpen: false, pengajuan: null, itemIds: [], itemsList: [] })}
                    className="px-4 py-2.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl cursor-pointer"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingRestore}
                    className="px-5 py-2.5 text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-xl shadow-md transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    {isSubmittingRestore ? (
                      <>
                        <span className="loading loading-spinner loading-xs"></span>
                        <span>Memproses...</span>
                      </>
                    ) : (
                      <>
                        <IconRotateClockwise size={16} />
                        <span>Kembalikan ke Etalase</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layouts>
  );
}
